// App.js
import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
 
// Context Providers
import { SignalRProvider } from './Contexts/signalR.js';
import { FileProvider } from './Contexts/FileContext.js';
import { PermissionsProvider } from './Contexts/Permissions.js';
import { ChatProvider } from './Contexts/ChatProvider.js';
// Components
import Login from './pages/Login/Login.jsx';
import Main from './pages/Main/Main.jsx';
import ClientDetails from './pages/ClientDetails/ClientDetails.jsx';
import AccountantDetails from './pages/AccountantDetails/AccountantDetails.jsx';
import Document from './pages/Document/Document.jsx';
import TransactionDocuments from './pages/TransactionDocuments/TransactionDocuments.jsx';
import OtherDocument from './pages/OtherDocuments/OtherDocument.jsx';
import Checks from './Components/Checks/Check.js';
import PaymentAcceptance from './pages/PaymentAcceptance/PaymentAcceptance.jsx';
import SubAccountant from './pages/SubAccountants/SubAccountants.jsx';
import ErrorPage from './Components/ErrorPage/ErrorPage.js';
import ProfitAndLossReport from './pages/ProfitAndLossReport/ProfitAndLossReport.jsx';
import SortCodeReport from './pages/SortCodeReport/SortCodeReport.jsx';
import CollectionPage from './pages/CollectionPage/CollectionPage.jsx';
import ReportTracking from './Components/ReportTrackingOld/ReportTrackingOld.jsx';
import LedgerReport from './pages/LedgerReport/LedgerReport.jsx';
import AccountCardDashboard from './pages/AccountCardDashboard/AccountCardDashboard.jsx';
import AccountCardsTable from './pages/AccountCardsTable/AccountCardsTable.jsx';
import JournalEntry from './pages/JournalEntryies/JournalEntry.jsx';
import AddClient from './pages/AddClient/AddClient.jsx';
import CreateInvoice from './pages/CreateInvoice/CreateInvoice.jsx';
import CreateReceipt from './pages/CreateInvoice/CreateReceipt.jsx';
import BankMatching from './pages/BankMatching/BankMatching.jsx';
import CardMatching from './pages/CardMatching/CardMatching.jsx';
import Assignments from './pages/Assignments/Assignments.jsx';
import ProtectedRoute from './Utils/ProtectedRoute.jsx';
import Unauthorized from './Components/Unauthorized/Unauthorized.jsx';
import BankPages from './pages/BankPages/BankPages.jsx';
// Styles and Themes
import './Services/translation.service.js';
import './App.css';
import 'react-toastify/dist/ReactToastify.css';
import 'primeicons/primeicons.css';
import 'primereact/resources/primereact.css';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primeflex/primeflex.css';
import AuthService from './Services/auth.service.js';
import { StyledEngineProvider } from '@mui/material/styles';
import { PrimeReactProvider } from 'primereact/api';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import { CacheProvider } from '@emotion/react';
import { arSD, heIL, enUS } from '@mui/x-data-grid/locales';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { NotificationContainer, NotificationManager } from 'react-notifications';
import { NavHistoryProvider } from './Contexts/NavHistoryContext.js';
import 'react-notifications/lib/notifications.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Assets from './pages/Assets/Assets.jsx';
import PrivacyPolicyModal from './Components/Widgets/PrivacyPolicyModal/PrivacyPolicyModal.js';
import PolicyService from './Services/policy.service.js';
import Constants from './assests/Constants/constants';
import HoldingItems from './pages/HoldingItems/HoldingItems.jsx';
import ClientItems from './pages/ClientItems/ClientItems.jsx';
import ThemeRoutes from './routes/ThemeRoutes.jsx';
import { HamburgerMenuProvider } from './Contexts/HamburgerMenuContext.js'; // Adjust path if needed
import { useFontSize } from './Contexts/FontSizeContext.js';
import PreloaderGuard from './Components/Preloader/PreloaderGuard.jsx';
import { stopAuthUrlListener } from './Utils/AuthFromUrl.js';
import AuthUrlWatcher from './Utils/AuthUrlWatcher.js';
import setupAxiosInterceptors from './Utils/axiosSetup.js';
import { installF12PasteFromPreviousRow } from './Utils/f12PasteFromPreviousRow.js'; 
function App() {
  const { i18n } = useTranslation();
  const { muiFontSize } = useFontSize();
  const [selectedLanguage, setSelectedLanguage] = useState(localStorage.getItem('language') || 'he');
  const [privacyAccepted, setPrivacyAccepted] = useState(true);
  const [consentAccepted, setConsentAccepted] = useState(true);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  // const initChatConf = async () => {
  //   await ChatService.InitChat();

  // }
  // Setup axios interceptors for payment verification (anti-tampering)
  useEffect(() => {
    setupAxiosInterceptors();
  }, []);

  // Install global F12 "paste from previous row" feature
  useEffect(() => {
    const uninstall = installF12PasteFromPreviousRow();
    return uninstall; // Cleanup on unmount
  }, []);
  
  useEffect(() => {
    // Check for saved language in localStorage
    const currentLanguage = localStorage.getItem('language') || 'he';
    i18n.changeLanguage(currentLanguage);
    
    // Cleanup on unmount
    return () => {
      stopAuthUrlListener();
    };
  }, []);
 
  useEffect(() => {
    const currentLanguage = localStorage.getItem('language') || 'he';
    setSelectedLanguage(currentLanguage);
  }, [i18n.language]);
 
  useEffect(() => {
    // Check if user is logged in
    const checkUser = async () => {
      const user = await AuthService.getCurrentUserTypeId();
      setIsLoggedIn(!!user); // Set logged-in state
    };
 
    checkUser();
  }, []);
 
  useEffect(() => {
    // Fetch policies acceptance status (privacy first, then consent)
    const checkPolicies = async () => {
      if (!isLoggedIn) {
        setLoading(false);
        return;
      }
 
      try {
        const [privacy, consent] = await Promise.all([
          PolicyService.hasAcceptedPolicy(Constants.PrivacyId),
          PolicyService.hasAcceptedPolicy(Constants.ConsentAndDataOwnerCommitmentForm)
        ]);
 
        setPrivacyAccepted(privacy);
        setConsentAccepted(consent);
 
        if (!privacy) {
          setShowPrivacyModal(true);
          setShowConsentModal(false);
        } else if (!consent) {
          setShowPrivacyModal(false);
          setShowConsentModal(true);
        } else {
          setShowPrivacyModal(false);
          setShowConsentModal(false);
        }
      } finally {
        setLoading(false);
      }
    };
 
    checkPolicies();
  }, [isLoggedIn]);
 
 
  const existingTheme = useTheme();
 
  const theme = useMemo(() => {
    let locale;
    let direction = 'ltr'; // Default direction

    switch (selectedLanguage) {
      case 'ar':
        locale = arSD;
        direction = 'rtl';
        break;
      case 'he':
        locale = heIL;
        direction = 'rtl';
        break;
      case 'en':
      default:
        locale = enUS;
        direction = 'ltr';
        break;
    }

    return createTheme({}, locale, existingTheme, {
      direction,
      typography: {
        fontSize: muiFontSize,
      },
      // MUI Modal/Dialog/Menu/Select lock body scroll by setting
      // `padding-right: 15px; overflow: hidden` on <body>. That shifts the
      // layout because html already uses `scrollbar-gutter: stable`.
      components: {
        MuiModal: { defaultProps: { disableScrollLock: true } },
        MuiPopover: { defaultProps: { disableScrollLock: true } },
        MuiMenu: { defaultProps: { disableScrollLock: true } },
        MuiDialog: { defaultProps: { disableScrollLock: true } },
        MuiDrawer: { defaultProps: { disableScrollLock: true } },
      },
    });
  }, [selectedLanguage, existingTheme, muiFontSize]);
 
  const cacheRtl = createCache({
    key: 'muirtl',
    stylisPlugins: [prefixer, rtlPlugin],
  });
 
  const cacheLtr = createCache({
    key: 'muiltr',
    stylisPlugins: [prefixer] // Removed rtlPlugin for strict LTR styling
  });
  const cache = useMemo(
    () =>
      createCache({
        key: selectedLanguage === 'en' ? 'muiltr' : 'muirtl',
        stylisPlugins:
          selectedLanguage === 'en' ? [prefixer] : [prefixer, rtlPlugin],
      }),
    [selectedLanguage]
  );
 
 
 
 
  return (
    <Router>
          <AuthUrlWatcher setIsLoggedIn={setIsLoggedIn} />
          <PrimeReactProvider>
            <StyledEngineProvider injectFirst>
              <CacheProvider value={cache}>
                <ThemeProvider theme={theme}>
                    <NavHistoryProvider>
                      <PermissionsProvider>
                      <HamburgerMenuProvider>
                        {isLoggedIn && !loading && showPrivacyModal && (
                          <PrivacyPolicyModal
                            visible={showPrivacyModal}
                            policyId={Constants.PrivacyId}
                            onAccept={() => {
                              setPrivacyAccepted(true);
                              setShowPrivacyModal(false);
                              if (!consentAccepted) {
                                setShowConsentModal(true);
                              }
                            }}
                          />
                        )}
                        {isLoggedIn && !loading && !showPrivacyModal && showConsentModal && (
                          <PrivacyPolicyModal
                            visible={showConsentModal}
                            policyId={Constants.ConsentAndDataOwnerCommitmentForm}
                            title={i18n.t('privacyPolicy.consentTitle', { defaultValue: 'Consent and Data Owner Commitment' })}
                            onAccept={() => {
                              setConsentAccepted(true);
                              setShowConsentModal(false);
                            }}
                          />
                        )}
                        <div className="App">
                          <PreloaderGuard>
                            <ThemeRoutes />
                          </PreloaderGuard>
                          <NotificationContainer />
                        </div>
                        </HamburgerMenuProvider>
                      </PermissionsProvider>
                    </NavHistoryProvider>
                </ThemeProvider>
              </CacheProvider>
            </StyledEngineProvider>
          </PrimeReactProvider>
      </Router>
 
 
  );
}
 
export default App;